import { useFormik } from "formik";
import * as Yup from "yup";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  useGetRemittanceRequestableDocTypesQuery,
  remittance,
} from "../../../../../store/queries/remittance";
import { requestAdditionalDocument } from "./api";

const requestItemSchema = Yup.object({
  document_type: Yup.string().required("*Document type is required"),
  comment: Yup.string().required("*Comment is required"),
});

const validationSchema = Yup.object({
  requests: Yup.array().of(requestItemSchema).min(1),
});

const useRequestForm = ({ handleClose }) => {
  const { remittanceID } = useParams();
  const dispatch = useDispatch();

  const { data: requestableDocTypes } =
    useGetRemittanceRequestableDocTypesQuery({
      remittance_id: remittanceID,
    });

  const formik = useFormik({
    initialValues: {
      requests: [{ document_type: "", comment: "" }],
    },
    validationSchema,
    onSubmit: async (values) => {
      const formData = new FormData();
      formData.append("remittance_id", remittanceID);
      values.requests.forEach((req, index) => {
        formData.append(`requests[${index}][comment]`, req.comment);
        formData.append(`requests[${index}][document_type]`, req.document_type);
      });

      try {
        const response = await requestAdditionalDocument(formData);
        if (response?.data?.success) {
          toast.success(
            response?.data?.message || "Document requested successfully"
          );
          dispatch(
            remittance.util.invalidateTags(["remittanceAdditionalRequests"])
          );
          handleClose();
        } else {
          toast.error(
            response?.data?.message ||
              response?.message ||
              "Failed to request document"
          );
        }
      } catch {
        toast.error("Failed to request document");
      }
    },
  });

  const requestableOptions = (requestableDocTypes?.data || []).map((item) => ({
    label: item?.document_type_label || item?.label || item?.name,
    value: item?.document_type || item?.value || item?.id,
  }));

  const handleAddRequest = () => {
    formik.setFieldValue("requests", [
      ...formik.values.requests,
      { document_type: "", comment: "" },
    ]);
  };

  const handleRemoveRequest = (index) => {
    const updated = formik.values.requests.filter((_, i) => i !== index);
    formik.setFieldValue("requests", updated);
  };

  // Check if all fields in all requests are filled
  const allFilled = formik.values.requests.every(
    (req) => req.document_type && req.comment
  );

  // Hide "Add More" if last item is incomplete or all doc types are used
  const lastRequest = formik.values.requests.at(-1);
  const selectedCount = formik.values.requests.filter((r) => r.document_type).length;
  const canAddMore =
    lastRequest?.document_type &&
    lastRequest?.comment &&
    selectedCount < requestableOptions.length;

  return {
    formik,
    requestableOptions,
    allFilled,
    canAddMore,
    handleAddRequest,
    handleRemoveRequest,
  };
};

export default useRequestForm;
